import React, { useEffect, useMemo, useRef, useState } from "react";
import ContentPasteGo from "@mui/icons-material/ContentPasteGo";
import SaveAltOutlined from "@mui/icons-material/SaveAltOutlined";
import { Alert } from "@mui/material";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Subheader from "@components/Subheader/Subheader";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import { useNotification } from "@context/NotificationContext";
import type { VesselNominationScanItem } from "@features/vessel-nomination/vessel-scan/VesselNominationScanResults.types";
import { usePersistentForm } from "@hooks/usePersistentForm";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { DryBulkNominationResponse } from "@services/dry-bulk-nomination/types";
import {
  createDryBulkNomination,
  discardDryBulkNomination,
  DRY_BULK_NOMINATION_QUERY_KEY,
  getDryBulkNomination,
  submitDryBulkNomination,
  updateDryBulkNomination,
  updateSubmittedDryBulkNomination,
} from "@services/dryBulkNominationApi";
import type { VesselNominationHeaderResponse } from "@services/vessel-nomination/types";
import { getVesselNominationHeader, QUERY_KEY } from "@services/vesselNominationApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { FormProvider, type FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ActivitiesSection,
  AttachmentsSection,
  CargoLinesSection,
  NominationSection,
  RemarksSection,
  ResourcesSection,
} from "./components/DryBulkNominationSections";
import DryBulkVesselDetails from "./components/DryBulkVesselDetails";
import ShortNoticeBanner from "./components/ShortNoticeBanner";
import DryBulkButton from "./components/ui/DryBulkButton";
import {
  emptyDryBulkNominationForm,
  getDryBulkNominationSchema,
  getDryBulkSubmissionSchema,
  type DryBulkNominationFormData,
} from "./schema/dryBulkNominationSchema";
import {
  DRY_BULK_NOMINATION_FORM_PERSIST_KEY,
  NEW_DRAFT_KEY,
  useDryBulkNominationFormStore,
} from "./store/useDryBulkNominationForm.store";
import {
  fromDryBulkNominationResponse,
  getFieldErrors,
  getHttpStatus,
  toDryBulkNominationRequest,
} from "./utils/dryBulkNominationMapping";
import { formatUaeDateTime } from "./utils/formatUaeDate";
import { isShortNoticeCall } from "./utils/isShortNoticeCall";

const LANG_BASE = "vessel-nomination.dry-bulk.add";
const LANG_BASE_DETAILS = "vessel-nomination.dry-bulk.details";
const TEST_ID_BASE = "dry-bulk-add-vessel-nomination";
const NAVIGATION_BLOCKER_SOURCE = "dry-bulk-nomination-form";
const GRID_PATH = `/${paths.vesselNomination.index}`;
const VESSEL_SCAN_PATH = `${GRID_PATH}/${paths.vesselNomination.vesselScan}`;
const DRY_BULK_PATH = `${GRID_PATH}/dry-bulk`;
const DRY_BULK_DRAFT_PATH = `${GRID_PATH}/${paths.vesselNomination.addDryBulk}`;
const TELEMETRY_MODULE = "dry_bulk_vessel_nomination";
const TELEMETRY_SCREEN = "DryBulkNominationForm";
const TELEMETRY_ENTITY_TYPE = "dry_bulk_nomination";
const transactionContext = (action: string, entityId?: string) => ({
  module: TELEMETRY_MODULE,
  screen: TELEMETRY_SCREEN,
  action,
  entityType: TELEMETRY_ENTITY_TYPE,
  ...(entityId ? { entityId } : {}),
});
const HEADER_FIELDS: FieldPath<DryBulkNominationFormData>[] = [
  "portCode",
  "lastPortCode",
  "nextPortCode",
  "initialEta",
  "maxArrivalDraft",
  "remarks",
  "expectedBerthCode",
  "purposeOfVisitCode",
  "viaNumber",
];

/**
 * Dry-bulk Add Vessel Nomination (VN-01…VN-10). `dry-bulk/add` starts a new draft from a DRY_BULK scan (vessel in
 * the route state); `dry-bulk/add/:id` reopens a saved draft (G-3). Save as Draft checks formats only (D5); unsaved
 * input is kept in the form store until it is saved or discarded (AC18). Request Nomination (DBVN-0007) checks the
 * submit rules, saves a new form as a draft first, then submits it; Cancel and menu navigation ask before unsaved
 * changes are dropped (7423).
 */
const DryBulkAddVesselNominationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const location = useLocation();
  const { id: draftId } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { userActionPermissions, userAccessPermissions } = useAuthStore();
  const [loadedAt] = useState(() => Date.now());
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);
  const { setNavigationBlocker } = useNavigationGuard();

  const scannedVessel = (location.state as { vessel?: VesselNominationScanItem } | undefined)
    ?.vessel;
  const canRequest = checkPermission(userActionPermissions, "canRequestDryBulkVesselNomination");
  const canRead = checkPermission(userAccessPermissions, "hasDryBulkVesselNominationAccess");
  const isUpdate = !!draftId && !!location.pathname?.includes("/dry-bulk/edit/");
  const detailsPath = `${DRY_BULK_PATH}/${draftId}`;
  const draftKey = draftId ? (isUpdate ? `edit:${draftId}` : draftId) : NEW_DRAFT_KEY;

  const store = useDryBulkNominationFormStore();
  const openedKey = useRef<string | null>(null);
  if (openedKey.current !== draftKey) {
    useDryBulkNominationFormStore
      .getState()
      .open(draftKey, draftId ? null : (scannedVessel?.id ?? null));
    openedKey.current = draftKey;
  }
  const storeSnapshot = useDryBulkNominationFormStore.getState();

  const draftQuery = useQuery<DryBulkNominationResponse>({
    queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, draftId],
    queryFn: () => getDryBulkNomination(draftId as string),
    enabled: (canRequest || canRead) && !!draftId,
  });
  const draft = draftQuery.data;
  const readOnly = !canRequest || (!!draft && draft.status !== "DRAFT" && !isUpdate);
  const vesselId = draftId
    ? draft?.vesselId
    : (scannedVessel?.id ?? storeSnapshot.vesselId ?? undefined);

  const schema = useMemo(() => getDryBulkNominationSchema(t), [t]);
  const submissionSchema = useMemo(() => getDryBulkSubmissionSchema(t), [t]);
  const defaults = useMemo(() => emptyDryBulkNominationForm(), []);
  const form = usePersistentForm<DryBulkNominationFormData>({
    schema,
    defaultValues: defaults,
    storeValues: storeSnapshot.values,
    setStoreValues: store.setAll,
    resetStore: store.resetForm,
    persistKey: DRY_BULK_NOMINATION_FORM_PERSIST_KEY,
    dateKeys: ["initialEta"],
  });

  const setStoreValues = store.setAll;
  useEffect(() => {
    const subscription = form.watch((values) =>
      setStoreValues(values as Partial<DryBulkNominationFormData>),
    );
    return () => subscription.unsubscribe();
  }, [form, setStoreValues]);

  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!draft) return;
    const hydrationKey = readOnly ? `${draft.id}:${draft.version}:${draft.status}` : draft.id;
    if (hydratedFor.current === hydrationKey) return;
    hydratedFor.current = hydrationKey;
    const { values, stale } = useDryBulkNominationFormStore.getState();
    if (readOnly || stale || Object.keys(values).length === 0) {
      void form.hydrate(() => Promise.resolve(fromDryBulkNominationResponse(draft)));
    }
  }, [draft, form, readOnly]);

  useEffect(() => {
    if (!canRequest) {
      navigate(draftId && canRead ? detailsPath : GRID_PATH, { replace: true });
    } else if (!draftId && !vesselId) {
      navigate(VESSEL_SCAN_PATH, { replace: true });
    } else if (
      draft &&
      (isUpdate
        ? draft.status !== "PENDING_APPROVAL" && draft.status !== "REJECTED"
        : draft.status !== "DRAFT")
    ) {
      navigate(detailsPath, { replace: true });
    }
  }, [canRequest, canRead, draftId, draft, isUpdate, vesselId, detailsPath, navigate]);

  useEffect(() => {
    if (draftQuery.isError) {
      notify({
        type: "error",
        title: t(`${LANG_BASE}.load-error-title`),
        description: t(`${LANG_BASE}.load-error-description`),
      });
      navigate(GRID_PATH, { replace: true });
    }
  }, [draftQuery.isError, navigate, notify, t]);

  const {
    data: header,
    isError: isHeaderError,
    isSuccess: isHeaderSuccess,
  } = useQuery<VesselNominationHeaderResponse>({
    queryKey: [QUERY_KEY, "vessel-header", vesselId],
    queryFn: () => getVesselNominationHeader(vesselId as string),
    enabled: (canRequest || canRead) && !!vesselId,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);
  useEffect(() => {
    if (hasTrackedInitialLoad.current || !isHeaderSuccess || (draftId && !draft)) {
      return;
    }
    hasTrackedInitialLoad.current = true;
    trackScreenLoad("Vessel Call Management", "Dry Bulk Nomination Form", startedAt.current);
  }, [isHeaderSuccess, draftId, draft]);

  useEffect(() => {
    if (isHeaderError) {
      notify({
        title: t(`${LANG_BASE}.header-error-title`),
        description: t(`${LANG_BASE}.header-error-description`),
        type: "error",
      });
    }
  }, [isHeaderError, notify, t]);

  const isDirty = form.formState.isDirty;
  const clearFormRef = useRef(form.clear);
  clearFormRef.current = form.clear;
  useEffect(() => {
    setNavigationBlocker(
      NAVIGATION_BLOCKER_SOURCE,
      isDirty && !readOnly
        ? {
            title: t(`${LANG_BASE}.leave-dialog.title`),
            description: t(`${LANG_BASE}.leave-dialog.description`),
            cancelButtonText: t(`${LANG_BASE}.leave-dialog.stay`),
            confirmButtonText: t(`${LANG_BASE}.leave-dialog.discard`),
            onConfirm: () => clearFormRef.current(),
          }
        : null,
    );
    return () => setNavigationBlocker(NAVIGATION_BLOCKER_SOURCE, null);
  }, [isDirty, readOnly, setNavigationBlocker, t]);

  const showSaveError = (error: unknown, titleKey = "save-error-title") => {
    const status = getHttpStatus(error);
    if (status === 409) {
      store.markStale();
      notify({
        type: "error",
        title: t(`${LANG_BASE}.conflict-title`),
        description: t(`${LANG_BASE}.conflict-description`),
      });
      return;
    }
    const fieldErrors = getFieldErrors(error);
    const headerErrors = Object.entries(fieldErrors).filter(([path]) =>
      HEADER_FIELDS.includes(path as FieldPath<DryBulkNominationFormData>),
    );
    headerErrors.forEach(([path, message]) =>
      form.setError(path as FieldPath<DryBulkNominationFormData>, { type: "server", message }),
    );
    notify({
      type: "error",
      title: t(`${LANG_BASE}.${titleKey}`),
      description:
        Object.keys(fieldErrors).length > headerErrors.length
          ? t(`${LANG_BASE}.save-error-lists`)
          : t(`${LANG_BASE}.save-error-description`),
    });
  };

  const save = useMutation({
    mutationFn: (values: DryBulkNominationFormData) =>
      runTransaction(
        transactionContext("save_dry_bulk_nomination_draft", draftId),
        (transactionId) => {
          const request = toDryBulkNominationRequest(values, vesselId as string, draft?.version);
          return draftId
            ? updateDryBulkNomination(draftId, request, { transactionId })
            : createDryBulkNomination(request, { transactionId });
        },
      ),
    onSuccess: (saved) => {
      queryClient.setQueryData([DRY_BULK_NOMINATION_QUERY_KEY, saved.id], saved);
      notify({
        type: "success",
        title: t(`${LANG_BASE}.saved-title`),
        description: t(`${LANG_BASE}.saved-description`, { reference: saved.refOrderNumber }),
      });
      if (draftId) {
        store.open(saved.id, null);
        store.setAll({});
        hydratedFor.current = null;
      } else {
        store.resetForm();
        navigate(`${DRY_BULK_DRAFT_PATH}/${saved.id}`, { replace: true });
      }
    },
    onError: (error) => showSaveError(error),
  });

  const createdForSubmit = useRef<string | null>(null);
  const submit = useMutation<DryBulkNominationResponse, unknown, DryBulkNominationFormData>({
    mutationFn: (values) =>
      runTransaction(
        transactionContext("submit_dry_bulk_nomination", draftId),
        async (transactionId) => {
          let id: string | undefined = draftId;
          let version = draft?.version;
          if (!id) {
            const created = await createDryBulkNomination(
              toDryBulkNominationRequest(values, vesselId as string, undefined),
              { transactionId },
            );
            createdForSubmit.current = created.id;
            id = created.id;
            version = created.version;
          }
          return submitDryBulkNomination(
            id as string,
            toDryBulkNominationRequest(values, vesselId as string, version),
            { transactionId },
          );
        },
      ),
    onSuccess: (submitted) => {
      createdForSubmit.current = null;
      form.clear();
      queryClient.removeQueries({ queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, submitted.id] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      notify({
        type: "success",
        title: t(`${LANG_BASE}.submitted-title`),
        description: "",
      });
      navigate(GRID_PATH, { replace: true });
    },
    onError: (error) => {
      const created = createdForSubmit.current;
      createdForSubmit.current = null;
      if (getHttpStatus(error) === 503) {
        notify({
          type: "error",
          title: t(`${LANG_BASE}.submit-error-title`),
          description: t(`${LANG_BASE}.submit-unavailable-description`),
        });
      } else {
        showSaveError(error, "submit-error-title");
      }
      if (created) {
        store.resetForm();
        navigate(`${DRY_BULK_DRAFT_PATH}/${created}`, { replace: true });
      }
    },
  });

  /** The submit rules mirrored in the browser (Request Nomination and Update Nomination share them). */
  const checkSubmission = (values: DryBulkNominationFormData): boolean => {
    const result = submissionSchema.safeParse(values);
    if (result.success) {
      return true;
    }
    const reasons = { header: false, cargoLines: false, activities: false };
    result.error.issues.forEach((issue) => {
      const path = issue.path.join(".") as FieldPath<DryBulkNominationFormData>;
      if (HEADER_FIELDS.includes(path)) {
        reasons.header = true;
        form.setError(path, { type: "submit", message: issue.message });
      } else if (issue.path[0] === "cargoLines") {
        reasons.cargoLines = true;
      } else if (issue.path[0] === "activities") {
        reasons.activities = true;
        form.setError("activities", { type: "submit", message: issue.message });
      }
    });
    const sentences = [
      reasons.header && "submit-error-description",
      reasons.cargoLines && "submit-error-cargo-lines",
      reasons.activities && "submit-error-activity-dates",
    ].filter((key): key is string => !!key);
    notify({
      type: "error",
      title: t(`${LANG_BASE}.submit-error-title`),
      description: sentences.map((key) => t(`${LANG_BASE}.${key}`)).join(" "),
    });
    return false;
  };

  const requestNomination = form.handleSubmit((values) => {
    if (checkSubmission(values)) {
      submit.mutate(values);
    }
  });

  const updateSubmitted = useMutation({
    mutationFn: (values: DryBulkNominationFormData) =>
      runTransaction(
        transactionContext(
          draft?.status === "REJECTED"
            ? "resubmit_dry_bulk_nomination"
            : "update_dry_bulk_nomination",
          draftId,
        ),
        (transactionId) =>
          updateSubmittedDryBulkNomination(
            draftId as string,
            toDryBulkNominationRequest(values, vesselId as string, draft?.version),
            { transactionId },
          ),
      ),
    onSuccess: (updated) => {
      form.clear();
      queryClient.setQueryData([DRY_BULK_NOMINATION_QUERY_KEY, updated.id], updated);
      notify({
        type: "success",
        title: t(`${LANG_BASE_DETAILS}.update-toast`),
        description: "",
      });
      navigate(
        detailsPath,
        updated.status === "REJECTED"
          ? { replace: true, state: { resubmitted: true } }
          : { replace: true },
      );
    },
    onError: (error) => {
      const status = getHttpStatus(error);
      if (status === 409 || status === 503) {
        void queryClient.invalidateQueries({ queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, draftId] });
      }
      if (status === 409) {
        store.markStale();
        const detail = (error as { response?: { data?: { detail?: string } } } | null)?.response
          ?.data?.detail;
        notify({
          type: "error",
          title: t(`${LANG_BASE_DETAILS}.update-conflict-title`),
          description: detail ?? t(`${LANG_BASE}.conflict-description`),
        });
        return;
      }
      showSaveError(error, "submit-error-title");
    },
  });

  const updateNomination = form.handleSubmit((values) => {
    if (checkSubmission(values)) {
      updateSubmitted.mutate(values);
    }
  });

  const cancel = () => {
    if (isDirty && !readOnly) {
      setIsLeaveOpen(true);
    } else {
      navigate(isUpdate ? detailsPath : GRID_PATH);
    }
  };

  const discard = useMutation({
    mutationFn: () =>
      runTransaction(
        transactionContext("discard_dry_bulk_nomination_draft", draftId),
        (transactionId) => discardDryBulkNomination(draftId as string, { transactionId }),
      ),
    onSuccess: () => {
      form.clear();
      queryClient.removeQueries({ queryKey: [DRY_BULK_NOMINATION_QUERY_KEY, draftId] });
      notify({
        type: "success",
        title: t(`${LANG_BASE}.discarded-title`),
        description: t(`${LANG_BASE}.discarded-description`),
      });
      navigate(GRID_PATH, { replace: true });
    },
    onError: (error) => {
      setIsDiscardOpen(false);
      showSaveError(error);
    },
  });

  if (!canRequest || (!draftId && !vesselId) || (draftId && !draft)) return null;

  const showShortNotice =
    isHeaderSuccess && isShortNoticeCall(header?.activeVesselCall?.eta, loadedAt);
  const expiresAt = readOnly || isUpdate ? undefined : draft?.draftExpiresAt;
  const busy = save.isPending || discard.isPending || submit.isPending || updateSubmitted.isPending;
  const pageTitle = isUpdate
    ? t(`${LANG_BASE_DETAILS}.update-title`)
    : t(`${LANG_BASE}.page-title`);
  return (
    <PageLayout dir={dir}>
      <Subheader title={pageTitle} />
      <div className="pt-8 pb-8 px-24 flex flex-col gap-6" data-testid={TEST_ID_BASE}>
        {showShortNotice && <ShortNoticeBanner testId={TEST_ID_BASE} />}
        {draft && expiresAt && (
          <Alert severity="info" data-testid={`${TEST_ID_BASE}-draft-expiry`}>
            {t(`${LANG_BASE}.draft-expiry`, {
              reference: draft.refOrderNumber,
              date: formatUaeDateTime(expiresAt),
            })}
          </Alert>
        )}
        <DryBulkVesselDetails testId={TEST_ID_BASE} header={isHeaderSuccess ? header : undefined} />
        <FormProvider {...form}>
          <form
            className="mx-auto flex w-full max-w-[1024px] flex-col gap-6"
            data-testid={`${TEST_ID_BASE}-form-column`}
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit((values) => save.mutate(values))();
            }}
          >
            <NominationSection testId={TEST_ID_BASE} readOnly={readOnly} />
            <CargoLinesSection testId={TEST_ID_BASE} readOnly={readOnly} />
            <ActivitiesSection testId={TEST_ID_BASE} readOnly={readOnly} />
            <ResourcesSection
              testId={TEST_ID_BASE}
              owner="OPERATIONS_DEPARTMENT"
              readOnly={readOnly}
            />
            <ResourcesSection testId={TEST_ID_BASE} owner="VESSEL" readOnly={readOnly} />
            <AttachmentsSection testId={TEST_ID_BASE} readOnly={readOnly} />
            <RemarksSection testId={TEST_ID_BASE} readOnly={readOnly} />
            <div className="flex items-center justify-between gap-4">
              <DryBulkButton
                type="button"
                variant="text"
                color="primary"
                testId={`${TEST_ID_BASE}-cancel`}
                label={readOnly ? t("common.back") : t(`${LANG_BASE}.cancel`)}
                onClick={cancel}
                disabled={busy}
              />
              <div className="flex items-center gap-4">
                {draftId && !readOnly && !isUpdate && (
                  <DryBulkButton
                    type="button"
                    variant="outlined"
                    color="error"
                    testId={`${TEST_ID_BASE}-discard`}
                    label={t(`${LANG_BASE}.discard`)}
                    onClick={() => setIsDiscardOpen(true)}
                    disabled={busy}
                  />
                )}
                {isUpdate && (
                  <DryBulkButton
                    type="button"
                    variant="contained"
                    color="primary"
                    testId={`${TEST_ID_BASE}-update-nomination`}
                    startIcon={<ContentPasteGo />}
                    label={t(`${LANG_BASE_DETAILS}.update-button`)}
                    onClick={() => void updateNomination()}
                    disabled={busy}
                  />
                )}
                {!readOnly && !isUpdate && (
                  <>
                    <DryBulkButton
                      type="submit"
                      variant="text"
                      color="primary"
                      testId={`${TEST_ID_BASE}-save-draft`}
                      startIcon={<SaveAltOutlined />}
                      label={t(`${LANG_BASE}.save-draft`)}
                      disabled={busy}
                    />
                    <DryBulkButton
                      type="button"
                      variant="contained"
                      color="primary"
                      testId={`${TEST_ID_BASE}-request-nomination`}
                      startIcon={<ContentPasteGo />}
                      label={t(`${LANG_BASE}.request-nomination`)}
                      onClick={() => void requestNomination()}
                      disabled={busy}
                    />
                  </>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
      <GenericDialog
        open={isDiscardOpen}
        testId={`${TEST_ID_BASE}-discard-dialog`}
        title={t(`${LANG_BASE}.discard-dialog.title`)}
        elementContent={t(`${LANG_BASE}.discard-dialog.description`)}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t(`${LANG_BASE}.discard`)}
        handleCloseDialog={() => setIsDiscardOpen(false)}
        confirmAction={() => discard.mutate()}
        isSubmitting={discard.isPending}
      />
      <GenericDialog
        open={isLeaveOpen}
        testId={`${TEST_ID_BASE}-leave-dialog`}
        title={t(`${LANG_BASE}.leave-dialog.title`)}
        elementContent={t(`${LANG_BASE}.leave-dialog.description`)}
        cancelButtonText={t(`${LANG_BASE}.leave-dialog.stay`)}
        confirmButtonText={t(`${LANG_BASE}.leave-dialog.discard`)}
        handleCloseDialog={() => setIsLeaveOpen(false)}
        confirmAction={() => {
          setIsLeaveOpen(false);
          form.clear();
          navigate(isUpdate ? detailsPath : GRID_PATH);
        }}
      />
    </PageLayout>
  );
};

export default DryBulkAddVesselNominationPage;
